/**
 * LayoutPanels — per-section layout controls.
 *   - SectionLayoutControls: generic per-section container/inner controls.
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { setPath } from "./setPath.js";

export function SectionLayoutControls({ sectionKey, cfg, setCfg, showCardFields = false, defaults = {} }) {
  const s = cfg[sectionKey] || {};
  const set = (k, v) => setCfg(setPath(cfg, [sectionKey, k], v));
  const setMany = (obj) => {
    let next = cfg;
    Object.entries(obj).forEach(([k, v]) => { next = setPath(next, [sectionKey, k], v); });
    setCfg(next);
  };
  const num = (v) => (v === "" || v == null ? 0 : Number(v));
  const D = {
    container_width: 0, container_height: 0, offset_x: 0, offset_y: 0,
    container_radius: 24, container_bg: "#020B2D", container_border: "#3DA5FF",
    container_glow_color: "#3DA5FF", container_glow_intensity: 40,
    card_height: 0, card_gap: 14,
    ...defaults,
  };

  const PRESETS = sectionKey === "phases_section"
    ? {
        compacto:   { label: "Compacto",   values: { inner_row_gap: 8,  num_circle_size: 32, phase_title_size: 14, phase_icon_size: 44, phase_desc_size: 11, container_radius: 18 } },
        confortavel:{ label: "Confortável (padrão)", values: { inner_row_gap: 16, num_circle_size: 44, phase_title_size: 18, phase_icon_size: 64, phase_desc_size: 13, container_radius: 24 } },
        espacoso:   { label: "Espaçoso",   values: { inner_row_gap: 28, num_circle_size: 60, phase_title_size: 22, phase_icon_size: 88, phase_desc_size: 16, container_radius: 30 } },
        widescreen: { label: "Widescreen",  values: { inner_row_gap: 22, num_circle_size: 52, phase_title_size: 20, phase_icon_size: 76, phase_desc_size: 14, container_radius: 28 } },
        gigante:    { label: "Gigante",    values: { inner_row_gap: 32, num_circle_size: 64, phase_title_size: 24, phase_icon_size: 140, phase_desc_size: 16, container_radius: 30 } },
        colossal:   { label: "Colossal",   values: { inner_row_gap: 40, num_circle_size: 72, phase_title_size: 28, phase_icon_size: 200, phase_desc_size: 18, container_radius: 32 } },
      }
    : {
        mais_compacto: { label: "Mais compacto", values: { inner_row_gap: 4,  card_icon_size: 28, card_title_size: 12, card_desc_size: 9,  card_button_size: 11, card_button_h: 28, container_radius: 16, card_gap: 6, card_height: 0 } },
        compacto:   { label: "Compacto",   values: { inner_row_gap: 8,  card_icon_size: 40, card_title_size: 16, card_desc_size: 11, card_button_size: 13, card_button_h: 34, container_radius: 22 } },
        confortavel:{ label: "Confortável (padrão)", values: { inner_row_gap: 16, card_icon_size: 56, card_title_size: 22, card_desc_size: 14, card_button_size: 16, card_button_h: 42, container_radius: 28 } },
        espacoso:   { label: "Espaçoso",   values: { inner_row_gap: 24, card_icon_size: 72, card_title_size: 28, card_desc_size: 16, card_button_size: 18, card_button_h: 52, container_radius: 32 } },
        cartaz:     { label: "Cartaz XL",  values: { inner_row_gap: 28, card_icon_size: 88, card_title_size: 32, card_desc_size: 18, card_button_size: 20, card_button_h: 60, container_radius: 36 } },
      };

  return (
    <div className="rounded-lg border border-cyan-100 bg-cyan-50/60 p-3 space-y-3" data-testid={`${sectionKey}-layout-controls`}>
      <p className="text-[11px] font-bold text-cyan-800 uppercase tracking-wide">Posição · Dimensão · Efeitos da caixa</p>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-2 space-y-1.5" data-testid={`${sectionKey}-presets`}>
        <p className="text-[11px] font-black text-amber-900 uppercase tracking-wide">⚡ Presets rápidos</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(PRESETS).map(([key, p]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMany(p.values)}
              className="px-3 py-1.5 rounded-md text-[11px] font-bold bg-amber-500 hover:bg-amber-600 text-white transition shadow-sm"
              data-testid={`${sectionKey}-preset-${key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-amber-800/80 mt-1">Aplica combinações pré-testadas de espaçamento + tamanhos em 1 clique. Podes depois ajustar campo a campo.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Largura máxima do bloco (px · 0 = auto)</Label>
          <Input type="number" min={0} max={1400} value={s.container_width ?? D.container_width} onChange={(e) => set("container_width", num(e.target.value))} data-testid={`${sectionKey}-cw`} />
        </div>
        <div>
          <Label className="text-xs">Altura do bloco (px · 0 = auto)</Label>
          <Input type="number" min={0} max={900} value={s.container_height ?? D.container_height} onChange={(e) => set("container_height", num(e.target.value))} data-testid={`${sectionKey}-ch`} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Offset X ({s.offset_x ?? 0}px)</Label>
          <Input type="range" min={-300} max={300} value={s.offset_x ?? 0} onChange={(e) => set("offset_x", Number(e.target.value))} data-testid={`${sectionKey}-ox`} />
        </div>
        <div>
          <Label className="text-xs">Offset Y ({s.offset_y ?? 0}px)</Label>
          <Input type="range" min={-600} max={300} value={s.offset_y ?? 0} onChange={(e) => set("offset_y", Number(e.target.value))} data-testid={`${sectionKey}-oy`} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Margem superior ({s.margin_top ?? 28}px)</Label>
          <Input type="range" min={-400} max={200} value={s.margin_top ?? 28} onChange={(e) => set("margin_top", Number(e.target.value))} data-testid={`${sectionKey}-mt`} />
          <p className="text-[10px] text-slate-400">valores negativos empurram a caixa para cima (sobrepõe secções acima)</p>
        </div>
        <div>
          <Label className="text-xs">Camada Z-index ({s.z_index ?? 50})</Label>
          <Input type="range" min={0} max={100} value={s.z_index ?? 50} onChange={(e) => set("z_index", Number(e.target.value))} data-testid={`${sectionKey}-zindex`} />
          <p className="text-[10px] text-slate-400">valores mais altos ficam à frente (imagem lateral é 40)</p>
        </div>
      </div>
      <div className={`grid grid-cols-1 ${showCardFields ? "sm:grid-cols-3" : "sm:grid-cols-1"} gap-3`}>
        <div>
          <Label className="text-xs">Raio de canto (px)</Label>
          <Input type="number" min={0} max={60} value={s.container_radius ?? D.container_radius} onChange={(e) => set("container_radius", num(e.target.value))} data-testid={`${sectionKey}-radius`} />
        </div>
        {showCardFields && (
          <>
            <div>
              <Label className="text-xs">Altura card interno (px · 0 = auto)</Label>
              <Input type="number" min={0} max={600} value={s.card_height ?? D.card_height} onChange={(e) => set("card_height", num(e.target.value))} data-testid={`${sectionKey}-card-h`} />
            </div>
            <div>
              <Label className="text-xs">Gap entre cards (px)</Label>
              <Input type="number" min={0} max={40} value={s.card_gap ?? D.card_gap} onChange={(e) => set("card_gap", num(e.target.value))} data-testid={`${sectionKey}-card-gap`} />
            </div>
          </>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Cor de fundo</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={s.container_bg || D.container_bg} onChange={(e) => set("container_bg", e.target.value)} className="w-10 h-9 rounded border" />
            <Input value={s.container_bg || ""} onChange={(e) => set("container_bg", e.target.value)} className="font-mono text-xs" data-testid={`${sectionKey}-bg`} />
          </div>
        </div>
        <div>
          <Label className="text-xs">Cor do rebordo</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={s.container_border || D.container_border} onChange={(e) => set("container_border", e.target.value)} className="w-10 h-9 rounded border" />
            <Input value={s.container_border || ""} onChange={(e) => set("container_border", e.target.value)} className="font-mono text-xs" data-testid={`${sectionKey}-border`} />
          </div>
        </div>
        <div>
          <Label className="text-xs">Cor do Glow</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={s.container_glow_color || D.container_glow_color} onChange={(e) => set("container_glow_color", e.target.value)} className="w-10 h-9 rounded border" />
            <Input value={s.container_glow_color || ""} onChange={(e) => set("container_glow_color", e.target.value)} className="font-mono text-xs" data-testid={`${sectionKey}-glow-color`} />
          </div>
        </div>
      </div>
      <div>
        <Label className="text-xs">Intensidade do Glow ({s.container_glow_intensity ?? D.container_glow_intensity}%)</Label>
        <Input type="range" min={0} max={100} value={s.container_glow_intensity ?? D.container_glow_intensity} onChange={(e) => set("container_glow_intensity", Number(e.target.value))} data-testid={`${sectionKey}-glow-intensity`} />
      </div>

      <div className="pt-2 border-t border-cyan-200">
        <p className="text-[11px] font-bold text-cyan-800 uppercase tracking-wide mb-2">Elementos internos · Espaçamento · Tamanhos</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Espaço entre linhas (px)</Label>
            <Input type="number" min={0} max={80} value={s.inner_row_gap ?? 16} onChange={(e) => set("inner_row_gap", num(e.target.value))} data-testid={`${sectionKey}-row-gap`} />
          </div>
        </div>
        {sectionKey === "phases_section" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 p-2 rounded-md bg-cyan-50 border border-cyan-100">
              <div className="col-span-full text-[11px] font-bold text-cyan-800 uppercase">Espaçamento vertical entre elementos</div>
              <div>
                <Label className="text-xs">Nº → Título (px)</Label>
                <Input type="number" min={0} max={80} value={s.gap_num_to_title ?? 8} onChange={(e) => set("gap_num_to_title", num(e.target.value))} data-testid={`${sectionKey}-gap-num-title`} />
              </div>
              <div>
                <Label className="text-xs">Título → Imagem (px)</Label>
                <Input type="number" min={0} max={120} value={s.gap_title_to_icon ?? (s.inner_row_gap ?? 16)} onChange={(e) => set("gap_title_to_icon", num(e.target.value))} data-testid={`${sectionKey}-gap-title-icon`} />
              </div>
              <div>
                <Label className="text-xs">Imagem → Descrição (px)</Label>
                <Input type="number" min={0} max={120} value={s.gap_icon_to_desc ?? (s.inner_row_gap ?? 16)} onChange={(e) => set("gap_icon_to_desc", num(e.target.value))} data-testid={`${sectionKey}-gap-icon-desc`} />
              </div>
              <div className="col-span-full text-[11px] font-bold text-cyan-800 uppercase mt-1">Espaço horizontal entre colunas</div>
              <div className="col-span-full">
                <Label className="text-xs">Espaço entre Fase 1 · 2 · 3 (px)</Label>
                <Input type="number" min={0} max={200} value={s.column_gap ?? 30} onChange={(e) => set("column_gap", num(e.target.value))} data-testid={`${sectionKey}-column-gap`} />
              </div>
            </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div>
              <Label className="text-xs">Tamanho do círculo numerado (px)</Label>
              <Input type="number" min={24} max={100} value={s.num_circle_size ?? 44} onChange={(e) => set("num_circle_size", num(e.target.value))} data-testid={`${sectionKey}-num-size`} />
            </div>
            <div>
              <Label className="text-xs">Tamanho do título de fase (px)</Label>
              <Input type="number" min={10} max={40} value={s.phase_title_size ?? 18} onChange={(e) => set("phase_title_size", num(e.target.value))} data-testid={`${sectionKey}-title-size`} />
            </div>
            <div>
              <Label className="text-xs">Tamanho dos ícones/emojis (px)</Label>
              <Input type="number" min={24} max={320} value={s.phase_icon_size ?? 64} onChange={(e) => set("phase_icon_size", num(e.target.value))} data-testid={`${sectionKey}-icon-size`} />
            </div>
            <div>
              <Label className="text-xs">Tamanho da descrição (px)</Label>
              <Input type="number" min={9} max={22} value={s.phase_desc_size ?? 13} onChange={(e) => set("phase_desc_size", num(e.target.value))} data-testid={`${sectionKey}-desc-size`} />
            </div>
            <div>
              <Label className="text-xs">Espaço entre linhas da descrição ({(s.phase_desc_line_height ?? 1.625).toFixed(2)}×)</Label>
              <Input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={s.phase_desc_line_height ?? 1.625}
                onChange={(e) => set("phase_desc_line_height", Number(e.target.value))}
                data-testid={`${sectionKey}-desc-line-height`}
              />
            </div>
          </div>
          </>
        )}
        {sectionKey === "online_section" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div>
              <Label className="text-xs">Tamanho do ícone (px)</Label>
              <Input type="number" min={24} max={120} value={s.card_icon_size ?? 56} onChange={(e) => set("card_icon_size", num(e.target.value))} data-testid={`${sectionKey}-icon-size`} />
            </div>
            <div>
              <Label className="text-xs">Tamanho do título VS ___ (px)</Label>
              <Input type="number" min={12} max={40} value={s.card_title_size ?? 22} onChange={(e) => set("card_title_size", num(e.target.value))} data-testid={`${sectionKey}-title-size`} />
            </div>
            <div>
              <Label className="text-xs">Tamanho da descrição (px)</Label>
              <Input type="number" min={9} max={22} value={s.card_desc_size ?? 14} onChange={(e) => set("card_desc_size", num(e.target.value))} data-testid={`${sectionKey}-desc-size`} />
            </div>
            <div>
              <Label className="text-xs">Tamanho do texto botão JOGAR (px)</Label>
              <Input type="number" min={10} max={26} value={s.card_button_size ?? 16} onChange={(e) => set("card_button_size", num(e.target.value))} data-testid={`${sectionKey}-btn-size`} />
            </div>
            <div>
              <Label className="text-xs">Altura do botão JOGAR (px)</Label>
              <Input type="number" min={28} max={80} value={s.card_button_h ?? 42} onChange={(e) => set("card_button_h", num(e.target.value))} data-testid={`${sectionKey}-btn-h`} />
            </div>
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-cyan-200">
        <p className="text-[11px] font-bold text-cyan-800 uppercase tracking-wide mb-2">Mancha pintada do título (banner) · Tamanho · Forma · Posição</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Cor da mancha (azul)</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={s.banner_paint_color || "#1B6FD1"} onChange={(e) => set("banner_paint_color", e.target.value)} className="w-10 h-9 rounded border" />
              <Input value={s.banner_paint_color || ""} onChange={(e) => set("banner_paint_color", e.target.value)} className="font-mono text-xs" data-testid={`${sectionKey}-paint`} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Cor dos salpicos (amarelo)</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={s.banner_splatter_color || "#FFD93D"} onChange={(e) => set("banner_splatter_color", e.target.value)} className="w-10 h-9 rounded border" />
              <Input value={s.banner_splatter_color || ""} onChange={(e) => set("banner_splatter_color", e.target.value)} className="font-mono text-xs" data-testid={`${sectionKey}-splatter`} />
            </div>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={s.banner_show_splatter !== false}
                onChange={(e) => set("banner_show_splatter", e.target.checked)}
                data-testid={`${sectionKey}-show-splatter`}
              />
              Mostrar salpicos laterais
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs">Largura (px · 0 = auto)</Label>
            <Input type="number" min={0} max={1000} value={s.banner_width ?? 0} onChange={(e) => set("banner_width", num(e.target.value))} data-testid={`${sectionKey}-banner-w`} />
          </div>
          <div>
            <Label className="text-xs">Altura (px · 0 = auto)</Label>
            <Input type="number" min={0} max={400} value={s.banner_height ?? 0} onChange={(e) => set("banner_height", num(e.target.value))} data-testid={`${sectionKey}-banner-h`} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs">Padding horizontal ({s.banner_padding_x ?? 40}px)</Label>
            <Input type="range" min={0} max={200} value={s.banner_padding_x ?? 40} onChange={(e) => set("banner_padding_x", Number(e.target.value))} data-testid={`${sectionKey}-banner-px`} />
          </div>
          <div>
            <Label className="text-xs">Padding vertical ({s.banner_padding_y ?? 12}px)</Label>
            <Input type="range" min={0} max={80} value={s.banner_padding_y ?? 12} onChange={(e) => set("banner_padding_y", Number(e.target.value))} data-testid={`${sectionKey}-banner-py`} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs">Offset X ({s.banner_offset_x ?? 0}px)</Label>
            <Input type="range" min={-300} max={300} value={s.banner_offset_x ?? 0} onChange={(e) => set("banner_offset_x", Number(e.target.value))} data-testid={`${sectionKey}-banner-ox`} />
          </div>
          <div>
            <Label className="text-xs">Offset Y ({s.banner_offset_y ?? 0}px)</Label>
            <Input type="range" min={-200} max={200} value={s.banner_offset_y ?? 0} onChange={(e) => set("banner_offset_y", Number(e.target.value))} data-testid={`${sectionKey}-banner-oy`} />
          </div>
        </div>
        {sectionKey === "online_section" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div className="sm:col-span-2">
              <Label className="text-xs">Distância do banner às caixas ({s.cards_offset_top ?? 56}px)</Label>
              <Input
                type="range"
                min={0}
                max={160}
                value={s.cards_offset_top ?? 56}
                onChange={(e) => set("cards_offset_top", Number(e.target.value))}
                data-testid={`${sectionKey}-cards-offset-top`}
              />
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs">Escala ({(s.banner_scale ?? 1).toFixed(2)}×)</Label>
            <Input type="range" min={0.4} max={2.5} step={0.05} value={s.banner_scale ?? 1} onChange={(e) => set("banner_scale", Number(e.target.value))} data-testid={`${sectionKey}-banner-scale`} />
          </div>
          <div>
            <Label className="text-xs">Rugosidade / Forma ({s.banner_roughness ?? 10})</Label>
            <Input type="range" min={0} max={30} step={1} value={s.banner_roughness ?? 10} onChange={(e) => set("banner_roughness", Number(e.target.value))} data-testid={`${sectionKey}-banner-rough`} />
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          Object.entries(D).forEach(([k, v]) => set(k, v));
          set("banner_paint_color", "#1B6FD1");
          set("banner_splatter_color", "#FFD93D");
          set("banner_show_splatter", true);
        }}
        className="text-[11px] font-bold text-cyan-700 hover:text-cyan-900 underline"
        data-testid={`${sectionKey}-reset`}
      >
        Repor valores padrão
      </button>
    </div>
  );
}
